import axios from "axios";
import ResponseAlerts from "../functions/ResponseAlerts";

const BASE_CONDO_USER = "access-control-service/condo-users";

const RESOURCES = {
	LIST_ALL_CONDO_USER_PAGINATED: (page, filters, organizationId, orderBy, limit, disabled) => {
		const params = new URLSearchParams({
			page,
			limit: limit ?? 8,
			searchBy: filters.search || "",
			orderBy: orderBy?.collumnTitle || "",
			orderDirection: orderBy?.orderDirection || "",
		});

		const filterParams = {
			unitExternalId: filters.unitExternalId,
			carPlate: filters.carPlate,
			carName: filters.carModel,
			carBrand: filters.carBrand,
			tagExternalId: filters.profileTag,
			isTempEnabled: filters.isTempEnabled,
			isTempExpired: filters.isTempExpired,
			tempStartDate: filters.tempStartDate,
			tempStartTime: filters.tempStartTime,
			tempEndDate: filters.tempEndDate,
			tempEndTime: filters.tempEndTime,
		};
		Object.entries(filterParams).forEach(([key, value]) => {
			if (value) {
				params.set(key, value);
			}
		});

		if (disabled !== null && disabled !== undefined) {
			params.append("disabled", disabled);
		}

		return `${BASE_CONDO_USER}/organizations/${organizationId}/paginated?${params.toString()}`;
	},
	LIST_CONDO_USER_BY_CONDO_PAGINATED: (page, searchText, organizationId, orderBy, idCondominium, disabled) => {
		const params = new URLSearchParams({
			page,
			limit: 8,
			searchBy: searchText,
			orderBy: orderBy?.collumnTitle || "",
			orderDirection: orderBy?.orderDirection || "",
		});

		if (disabled !== null && disabled !== undefined) {
			params.append("disabled", disabled);
		}

		return `${BASE_CONDO_USER}/organizations/${organizationId}/condominiums/${idCondominium}/paginated?${params.toString()}`;
	},
	LIST_CONDO_USER_LICENSES: (condoUserId) => `${BASE_CONDO_USER}/${condoUserId}/licenses`,
	LIST_CONDO_USER_TAGS: (condoUserId) => `${BASE_CONDO_USER}/${condoUserId}/tags`,
	GET_BY_UUID: (uuid) => `${BASE_CONDO_USER}/uuid/${uuid}`,
	GET_BY_UUID_AND_CONDO: (uuid, unitId) => `${BASE_CONDO_USER}/condominium/${unitId}/uuid/${uuid}`,
	GET_BY_DOCUMENT_AND_CONDO: (document, unitId) => `${BASE_CONDO_USER}/condominium/${unitId}/document/${document}`,
	DELETE_CONDO_USER: (id) => `${BASE_CONDO_USER}/${id}`,
};

class CondoUserService {
	static async listCondoUsersPaginated(
		page,
		filters,
		organizationId,
		orderBy,
		limit,
		cancelToken,
		disabled = null,
	) {
		try {
			return await axios.get(
				RESOURCES.LIST_ALL_CONDO_USER_PAGINATED(
					page,
					typeof filters === "string" ? { search: filters } : (filters || {}),
					organizationId,
					orderBy,
					limit,
					disabled
				),
				{
					signal: cancelToken,
					headers: {
						"Cache-Control": "no-cache",
					},
				}
			);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar usuários do condomínio",
			});
			return error.response;
		}
	}

	static async listCondoUsersByCondoPaginated(
		page,
		searchText,
		organizationId,
		orderBy,
		cancelToken,
		idCondominium,
		disabled = null,
	) {
		try {
			return await axios.get(
				RESOURCES.LIST_CONDO_USER_BY_CONDO_PAGINATED(
					page,
					searchText,
					organizationId,
					orderBy,
					idCondominium,
					disabled
				),
				{
					signal: cancelToken,
					headers: {
						"Cache-Control": "no-cache",
					},
				}
			);
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar usuários do condomínio",
			});
			return error.response;
		}
	}

	static async listCondoUserLicenses(condoUserId) {
		try {
			const response = await axios.get(RESOURCES.LIST_CONDO_USER_LICENSES(condoUserId));
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar licenças do usuário",
			});
			return error.response;
		}
	}

	static async listCondoUserTags(condoUserId) {
		try {
			const response = await axios.get(RESOURCES.LIST_CONDO_USER_TAGS(condoUserId));
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao listar perfis do usuário",
			});
			return error.response;
		}
	}

	static async getByUUIDCondoUser(uuid) {
		try {
			const response = await axios.get(RESOURCES.GET_BY_UUID(uuid));
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao obter usuário",
			});
			return error.response;
		}
	}

	static async getByCondoAndDocument(document, unitId) {
		try {
			const response = await axios.get(RESOURCES.GET_BY_DOCUMENT_AND_CONDO(document, unitId));
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao obter usuário",
			});
			return error.response;
		}
	}

	static async getByCondoAndUuid(uuid, unitId) {
		try {
			const response = await axios.get(RESOURCES.GET_BY_UUID_AND_CONDO(uuid, unitId));
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao obter usuário",
			});
			return error.response;
		}
	}

	static async createAccess(payload) {
		try {
			const response = await axios.post("access-control-service/access", payload);
			ResponseAlerts({
				response: response,
				successMessage: `${payload.createUser ? 'Usuário' : 'Acesso'} cadastrado com sucesso`,
			});
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: `Erro ao cadastrar ${payload.createUser ? 'usuário' : 'acesso'}`,
			});
			return error.response;
		}
	}

	static async createCondoUser(payload) {
		try {
			const response = await axios.post(BASE_CONDO_USER, payload);
			ResponseAlerts({
				response: response,
				successMessage: "Usuário cadastrado com sucesso",
			});
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao cadastrar usuário do condomínio",
			});
			return error.response;
		}
	}

	static async deleteCondoUser(id) {
		try {
			const response = await axios.delete(RESOURCES.DELETE_CONDO_USER(id));
			ResponseAlerts({
				response: response,
				successMessage: "Usuário excluído com sucesso",
			});
			return response;
		} catch (error) {
			console.log(error);
			ResponseAlerts({
				response: error.response,
				errorMessage: "Erro ao excluir usuário",
			});
			return error.response;
		}
	}

	static async updateCondoUser(id, payload) {
		return await axios
			.put(BASE_CONDO_USER + "/" + id, payload)
			.then((response) => {
				ResponseAlerts({
					response: response,
					successMessage: "Usuário atualizado com sucesso",
				});
				return response;
			})
			.catch((error) => {
				console.log(error);
				ResponseAlerts({
					response: error.response,
					errorMessage: "Erro ao atualizar usuário",
				});
				return error.response;
			});
	}
}

export default CondoUserService;
